import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import './OrderDialog.css';
import { getOrderDropdownTickers, getTickerPrice, getSettings } from '../services/api';
import { getCurrentLocalDate, formatISODate } from '../utils/dateUtils';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger('orderDialog', 'Order entry dialog for manual orders', false, 'DEBUG');

const ORDER_TYPES = [
  { value: 'LIMIT', label: 'Limit' },
  { value: 'STOP_LOSS', label: 'Stop loss' }
];

const POSITION_SIZES = [
  { value: 0.25, label: '0.25x' },
  { value: 0.125, label: '0.125x' }
];

const DEFAULT_MAX_TICKERS = 15;

function OrderDialog({ show, onHide, onSave, accounts = [], initialAccountId = '', setAccountId, ticker = '', account: singleAccount, defaultAction = 'BUY', preloadedTicker = null, ...props }) {
  // If singleAccount is provided, use it and hide the dropdown
  const [accountId, setLocalAccountId] = useState(initialAccountId || (accounts[0]?.account_id || singleAccount?.account_id || ''));
  const account = singleAccount || accounts.find(acc => acc.account_id === accountId) || accounts[0] || {};
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [type, setType] = useState('LIMIT');
  const [price, setPrice] = useState('');
  const [, setIsEditingPrice] = useState(false);
  const [positionSize, setPositionSize] = useState(1.0);
  const [error, setError] = useState('');
  const [action, setAction] = useState(defaultAction || 'BUY'); // New state for Buy/Sell
  const [orderDate, setOrderDate] = useState(getCurrentLocalDate()); // Date field with today as default
  const [priceAutoFilled, setPriceAutoFilled] = useState(false);
  const priceAutoFilledRef = useRef(priceAutoFilled);
  const initializationContextRef = useRef(null);
  
  // Use external error prop if provided, otherwise use internal error state
  const displayError = props.error || error;
  const [dropdownTickers, setDropdownTickers] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isValidSymbol, setIsValidSymbol] = useState(true);
  const [sellPercent, setSellPercent] = useState(100);
  const sellPercentSourceRef = useRef('init');
  const [maxTickersPerAccount, setMaxTickersPerAccount] = useState(DEFAULT_MAX_TICKERS);

  const editingOrder = props.initialData || null;
  const editingOrderId = editingOrder?.order_id ?? null;
  const editingSymbol = editingOrder?.symbol ?? '';
  const editingQuantity = editingOrder?.quantity ?? '';
  const editingType = editingOrder?.type ?? 'LIMIT';
  const editingPrice = editingOrder?.price;
  const editingAction = editingOrder?.action ?? 'BUY';
  const editingOrderDate = editingOrder?.order_date ?? null;

  const resolvedDefaultAction = defaultAction || 'BUY';
  const preloadedSymbol = preloadedTicker?.symbol ?? '';
  const preloadedPrice = preloadedTicker?.current_price ?? null;
  const preloadedShares = preloadedTicker?.shares ?? null;
  const hasPreloadedTicker = Boolean(preloadedTicker);

  // Helper: get tickers with shares > 0
  const heldTickers = props.tickers.filter(t => t.shares > 0);

  const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const sanitized = String(value).replace(/[^0-9.-]/g, '');
    if (!sanitized || sanitized === '-' || sanitized === '.' || !/[0-9]/.test(sanitized)) {
      return null;
    }

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const resolveHeldShares = useCallback((symbolValue) => {
    if (!symbolValue) {
      return null;
    }

    const held = heldTickers.find(t => t.symbol === symbolValue);
    return parseNumericValue(held?.shares ?? held?.current_shares ?? held?.quantity ?? 0);
  }, [heldTickers]);

  // Determine which tickers to show in dropdown
  const tickerOptions = action === 'SELL' ? heldTickers : dropdownTickers;

  // Check if ticker is preloaded (readonly mode)
  const isTickerPreloaded = ticker && ticker.trim() !== '';
  const shouldRestrictBuySymbol = action === 'BUY' && !isTickerPreloaded && !editingOrder;

  // Filter tickers based on search term for BUY orders
  const filteredTickers = action === 'BUY' 
    ? dropdownTickers.filter(ticker => 
        ticker.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticker.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : dropdownTickers;

  // Get max shares for selected symbol (for sell validation)
  const maxSellShares = action === 'SELL' ? (resolveHeldShares(symbol) ?? 0) : null;
  
  // Compute Gain/Loss for SELL preview
  const selectedHeld = heldTickers.find(t => t.symbol === symbol);
  const costBasis = selectedHeld?.cost_basis || null;
  const gainLossPerShare = (action === 'SELL' && price && costBasis != null) ? (Number(price) - Number(costBasis)) : null;
  const gainLossTotal = (gainLossPerShare != null && quantity) ? (gainLossPerShare * Number(quantity)) : null;

  useEffect(() => {
    priceAutoFilledRef.current = priceAutoFilled;
  }, [priceAutoFilled]);

  // Fetch dropdown tickers when dialog opens
  useEffect(() => {
    if (show && action === 'BUY') {
      fetchDropdownTickers();
    }
  }, [show, action]);

  useEffect(() => {
    if (!show) {
      return;
    }

    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        const value = response?.data?.max_tickers_per_account;
        const parsed = parseInt(value, 10);
        if (isMounted && !Number.isNaN(parsed) && parsed > 0) {
          setMaxTickersPerAccount(parsed);
        } else if (isMounted) {
          setMaxTickersPerAccount(DEFAULT_MAX_TICKERS);
        }
      } catch (error) {
        logger.error('Error fetching settings for order dialog:', error);
        if (isMounted) {
          setMaxTickersPerAccount(DEFAULT_MAX_TICKERS);
        }
      }
    };

    fetchSettings();

    return () => {
      isMounted = false;
    };
  }, [show]);

  // Debug: log dropdown tickers to see what data is available
  useEffect(() => {
    if (dropdownTickers.length > 0) {
      logger.debug('🔍 Sample dropdown ticker data:', dropdownTickers[0]);
    }
  }, [dropdownTickers]);
  
  // Clear internal error when external error is cleared
  useEffect(() => {
    if (!props.error) {
      setError('');
    }
  }, [props.error]);

  const fetchDropdownTickers = async () => {
    setDropdownLoading(true);
    try {
      const response = await getOrderDropdownTickers();
      setDropdownTickers(response.data.tickers || []);
    } catch (error) {
      logger.error('Error fetching dropdown tickers:', error);
      setDropdownTickers([]);
    } finally {
      setDropdownLoading(false);
    }
  };

  const formatPriceValue = useCallback((rawPrice) => {
    const parsed = Number(rawPrice);
    if (Number.isFinite(parsed)) {
      return parsed.toFixed(2);
    }
    return String(rawPrice ?? '');
  }, []);

  const attemptAutofillPrice = useCallback((rawPrice) => {
    if (rawPrice === null || rawPrice === undefined) {
      return false;
    }
    if (typeof rawPrice === 'string' && rawPrice.trim() === '') {
      return false;
    }
    if (typeof rawPrice === 'number' && !Number.isFinite(rawPrice)) {
      return false;
    }
    if (priceAutoFilledRef.current) {
      return false;
    }
    setPrice(formatPriceValue(rawPrice));
    setPriceAutoFilled(true);
    priceAutoFilledRef.current = true;
    return true;
  }, [formatPriceValue]);

  const handlePriceChange = (value) => {
    setPrice(value);
    if (!priceAutoFilledRef.current) {
      setPriceAutoFilled(true);
      priceAutoFilledRef.current = true;
    }
  };

  const handlePriceBlur = () => {
    setIsEditingPrice(false);
    if (price === '' || price === null || price === undefined) {
      return;
    }
    const parsed = Number(price);
    if (Number.isFinite(parsed)) {
      setPrice(parsed.toFixed(2));
    }
  };

  const handlePriceFocus = () => {
    setIsEditingPrice(true);
  };

  const handleTickerSelect = async (selectedTicker) => {
    logger.debug('🔥 handleTickerSelect called for:', selectedTicker.symbol);

    setSymbol(selectedTicker.symbol);
    setSearchTerm(selectedTicker.symbol);
    setIsValidSymbol(true);
    setShowDropdown(false);
    setPrice('');
    setPriceAutoFilled(false);
    priceAutoFilledRef.current = false;

    // Preload current price when ticker is selected
    logger.debug('📊 Checking price sources for:', selectedTicker.symbol);

    if (selectedTicker.current_price != null) {
      logger.debug('✅ Found current_price in selectedTicker:', selectedTicker.current_price);
      if (attemptAutofillPrice(selectedTicker.current_price)) {
        return;
      }
    }

    // For new orders without preloaded ticker, try to find current price from tickers list
    const tickerFromList = props.tickers?.find(t => t.symbol === selectedTicker.symbol);
    logger.debug('🔍 Searching in props.tickers for:', selectedTicker.symbol, 'Found:', tickerFromList);

    if (tickerFromList && tickerFromList.current_price != null) {
      logger.debug('✅ Found current_price in tickerFromList:', tickerFromList.current_price);
      if (attemptAutofillPrice(tickerFromList.current_price)) {
        return;
      }
    }

    // Fallback: call backend API to get ticker price
    try {
      logger.debug('🔗 Calling backend API for price:', selectedTicker.symbol);
      const response = await getTickerPrice(selectedTicker.symbol);

      if (response.data && response.data.current_price != null) {
        logger.debug('✅ Got price from API:', response.data.current_price);
        if (attemptAutofillPrice(response.data.current_price)) {
          return;
        }
      }
    } catch (error) {
      logger.error('❌ API call failed:', error.message);
    }

    logger.debug('💡 No price source found - user will need to enter manually');
  };

  const validateSymbol = (inputSymbol) => {
    if (shouldRestrictBuySymbol && inputSymbol.trim()) {
      const isValid = dropdownTickers.some(ticker => 
        ticker.symbol.toUpperCase() === inputSymbol.trim().toUpperCase()
      );
      setIsValidSymbol(isValid);
    } else {
      setIsValidSymbol(true);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.ticker-search-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (singleAccount) {
      setLocalAccountId(singleAccount.account_id);
    } else if (initialAccountId) {
      setLocalAccountId(initialAccountId);
    }
  }, [singleAccount, initialAccountId]);

  useEffect(() => {
    if (!show) {
      initializationContextRef.current = null;
      return;
    }

    const contextKey = editingOrderId !== null
      ? `edit-${editingOrderId}`
      : `new-${resolvedDefaultAction}-${ticker || ''}-${preloadedSymbol}`;

    if (initializationContextRef.current === contextKey) {
      return;
    }

    initializationContextRef.current = contextKey;

    setPriceAutoFilled(false);
    priceAutoFilledRef.current = false;

    if (editingOrderId !== null) {
      setSymbol(editingSymbol);
      setSearchTerm(editingSymbol);
      setQuantity(editingQuantity || '');
      setType(editingType || 'LIMIT');
      if (!attemptAutofillPrice(editingPrice)) {
        setPrice(editingPrice != null && editingPrice !== '' ? formatPriceValue(editingPrice) : '');
      }
      setAction(editingAction);
      if (editingOrderDate) {
        const parsedDate = new Date(editingOrderDate);
        if (!Number.isNaN(parsedDate.getTime())) {
          // Preserve the order's own date when editing -- previously this
          // fell through to today's date regardless of parse success,
          // silently overwriting the order's date on every edit/save.
          setOrderDate(formatISODate(parsedDate));
        } else {
          setOrderDate(getCurrentLocalDate());
        }
      } else {
        setOrderDate(getCurrentLocalDate());
      }
      if (account?.standard_size && editingPrice && editingQuantity) {
        const calculatedPositionSize = (Number(editingPrice) * Number(editingQuantity)) / account.standard_size;
        const closest = POSITION_SIZES.reduce((prev, curr) =>
          Math.abs(curr.value - calculatedPositionSize) < Math.abs(prev.value - calculatedPositionSize) ? curr : prev
        );
        setPositionSize(closest.value);
      } else {
        setPositionSize(1.0);
      }
      if (editingAction === 'SELL') {
        const maxShares = resolveHeldShares(editingSymbol);
        const qty = parseNumericValue(editingQuantity);
        const sellOptions = [25, 50, 75, 100];
        let nextSellPercent = 100;

        if (Number.isFinite(maxShares) && maxShares > 0 && Number.isFinite(qty)) {
          const ratio = (qty / maxShares) * 100;
          nextSellPercent = sellOptions.reduce((closest, value) => (
            Math.abs(value - ratio) < Math.abs(closest - ratio) ? value : closest
          ), sellOptions[0]);
        }

        sellPercentSourceRef.current = 'init';
        setSellPercent(nextSellPercent);
      } else {
        sellPercentSourceRef.current = 'init';
        setSellPercent(100);
      }
    } else {
      const startingSymbol = ticker || '';
      setSymbol(startingSymbol);
      setSearchTerm(startingSymbol);
      setQuantity('');
      setType('LIMIT');
      if (!(hasPreloadedTicker && attemptAutofillPrice(preloadedPrice))) {
        setPrice('');
      }
      if (resolvedDefaultAction === 'SELL' && preloadedShares != null) {
        setQuantity(String(preloadedShares));
      }
      setPositionSize(1.0);
      setAction(resolvedDefaultAction);
      sellPercentSourceRef.current = 'init';
      setSellPercent(100);
      setOrderDate(getCurrentLocalDate());
    }
    setError('');
    setShowDropdown(false);
    setIsValidSymbol(true);
  }, [
    show,
    editingOrderId,
    editingSymbol,
    editingQuantity,
    editingType,
    editingPrice,
    editingAction,
    editingOrderDate,
    account?.account_id,
    account?.standard_size,
    ticker,
    resolvedDefaultAction,
    preloadedSymbol,
    preloadedPrice,
    preloadedShares,
    hasPreloadedTicker,
    attemptAutofillPrice,
    resolveHeldShares,
    formatPriceValue
  ]);

  const calculateBuyQuantity = (sizeValue, priceValue) => {
    if (!account?.standard_size) {
      return null;
    }

    const resolvedPrice = parseNumericValue(priceValue);
    if (!resolvedPrice || resolvedPrice <= 0) {
      return null;
    }

    const targetAmount = account.standard_size * sizeValue;
    const calculatedQuantity = targetAmount / resolvedPrice;
    return Math.floor(calculatedQuantity / 10) * 10;
  };

  const handlePositionSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setPositionSize(nextSize);

    if (action !== 'BUY') {
      return;
    }

    const nextQuantity = calculateBuyQuantity(nextSize, price);
    if (nextQuantity !== null) {
      setQuantity(nextQuantity);
    }
  };

  // Calculate quantity when price or position size changes
  const isEditingExistingOrder = Boolean(editingOrder);
  useEffect(() => {
    if (action === 'BUY' && price && account?.standard_size && positionSize && !isEditingExistingOrder) {
      const targetAmount = account.standard_size * positionSize;
      const calculatedQuantity = targetAmount / Number(price);
      // Round down to nearest 10
      setQuantity(Math.floor(calculatedQuantity / 10) * 10);
    }
  }, [action, price, positionSize, account?.standard_size, isEditingExistingOrder]);

  // Reset symbol if switching to SELL and current symbol is not held (skip if preloaded)
  useEffect(() => {
    if (action === 'SELL' && symbol && !heldTickers.some(t => t.symbol === symbol) && !isTickerPreloaded) {
      setSymbol(heldTickers[0]?.symbol || '');
    }
    // Removed BUY auto-set logic to prevent overwriting user input
    setIsValidSymbol(true); // Reset validation when action changes
  }, [action, heldTickers, tickerOptions, symbol, isTickerPreloaded]);

  const handleSellPercentSelect = (value) => {
    sellPercentSourceRef.current = 'user';
    setSellPercent(value);
  };

  // When in SELL mode, default quantity to a percentage of maxSellShares (100% by default)
  useEffect(() => {
    if (action !== 'SELL') return;
    if (!symbol) return;
    if (maxSellShares == null) return;
    if (editingOrder && sellPercentSourceRef.current !== 'user') return;
    const computed = Math.floor(Number(maxSellShares) * (Number(sellPercent) / 100));
    if (isFinite(computed)) {
      setQuantity(String(computed));
    }
  }, [action, symbol, maxSellShares, sellPercent, editingOrder]);

  const handleSave = (e) => {
    e.preventDefault();
    if (!symbol.trim()) {
      setError('Symbol is required.');
      return;
    }
    
    // Validate that the symbol is from the dropdown for BUY orders (skip if preloaded)
    if (shouldRestrictBuySymbol) {
      const isValidTicker = dropdownTickers.some(ticker => 
        ticker.symbol.toUpperCase() === symbol.trim().toUpperCase()
      );
      if (!isValidTicker) {
        setError('Please select a valid ticker from the dropdown. You cannot enter random ticker symbols.');
        return;
      }
    }
    if (!quantity || isNaN(quantity) || Number(quantity) <= 0) {
      setError('Quantity must be a positive number.');
      return;
    }
    if (!price || isNaN(price) || Number(price) <= 0) {
      setError('Price must be a positive number.');
      return;
    }
    // BUY: Enforce ticker count limit (but allow size adjustments regardless of cash balance)
    if (action === 'BUY') {
      const tickerExists = props.tickers.some(t => t.symbol.toUpperCase() === symbol.trim().toUpperCase());
      const tickersCountIfAdded = tickerExists ? props.tickers.length : props.tickers.length + 1;
      if (tickersCountIfAdded > maxTickersPerAccount) {
        setError(`You cannot have more than ${maxTickersPerAccount} tickers in your account. Sell or remove a position before adding a new one.`);
        return;
      }
    }
    // SELL: Prevent selling more than owned
    if (action === 'SELL') {
      if (Number(quantity) > maxSellShares) {
        setError(`You cannot sell more shares than you own (${maxSellShares}).`);
        return;
      }
    }
    setError('');
    onSave({
      symbol: symbol.trim().toUpperCase(),
      quantity: Number(quantity),
      action,
      type,
      price: Number(price),
              order_date: new Date(orderDate + 'T00:00:00').toISOString() // Use local time instead of UTC
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ paddingTop: 8, paddingBottom: 8 }}>
        <Modal.Title style={{ fontSize: '1.1rem' }}>{props.initialData ? 'Edit Order' : 'New Order'}{ticker ? ` for ${ticker}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="order-dialog-body-hue">
        {displayError && <Alert variant="danger">{displayError}</Alert>}
        
        <Row>
          {/* Position Sizing Guidelines - Left Column */}
          <Col xs={12} md={5}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <h6 className="mb-3" style={{ color: '#007bff', fontWeight: '600' }}>
                <i className="bi bi-info-circle me-2"></i>
                Position Sizing Guidelines
              </h6>
              <div className="table-responsive flex-grow-1" style={{ display: 'flex', flexDirection: 'column' }}>
                <table className="table table-sm table-bordered flex-grow-1" style={{ height: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Max Account Risk</td>
                      <td style={{ fontWeight: '500' }}>-2.5%</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Max Stop</td>
                      <td style={{ fontWeight: '500' }}>-10%</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Losses Average</td>
                      <td style={{ fontWeight: '500' }}>-5%</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Max Position</td>
                      <td style={{ fontWeight: '500' }}>50%</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Optimal Position</td>
                      <td style={{ fontWeight: '500' }}>20%-25%</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Max Tickers</td>
                      <td style={{ fontWeight: '500' }}>15</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '600', backgroundColor: '#f8f9fa' }}>Volume</td>
                      <td style={{ fontWeight: '500' }}>&gt; 500,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
          
          {/* Order Form - Right Column */}
          <Col xs={12} md={7}>
            <Form onSubmit={handleSave}>
          <Row className="mb-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Account</Form.Label>
                {singleAccount ? (
                  <Form.Control 
                    type="text" 
                    value={`${singleAccount.name} ($${singleAccount.cash?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'})`} 
                    readOnly 
                  />
                ) : (
                  <Form.Select
                    value={accountId}
                    onChange={e => {
                      setLocalAccountId(e.target.value);
                      setAccountId && setAccountId(e.target.value);
                    }}
                  >
                    {accounts.map(acc => (
                      <option key={acc.account_id} value={acc.account_id}>
                        {acc.name} (Cash: ${acc.cash?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
          </Row>
          {action === 'SELL' && symbol && (
            <Row className="mb-2">
              <Col xs={12}>
                <div className="d-flex justify-content-between small">
                  <span className="text-muted">Gain/Loss</span>
                  <span className={`${gainLossTotal != null ? (gainLossTotal >= 0 ? 'text-success' : 'text-danger') : 'text-muted'}`}>
                    {gainLossTotal != null ? `${gainLossTotal >= 0 ? '' : '-'}$${Math.abs(gainLossTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                    {gainLossPerShare != null ? ` (${gainLossPerShare >= 0 ? '+' : '-'}$${Math.abs(gainLossPerShare).toFixed(2)}/sh)` : ''}
                  </span>
                </div>
              </Col>
            </Row>
          )}
          <Row className="mb-3">
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Symbol</Form.Label>
                {action === 'SELL' ? (
                  <Form.Select
                    value={symbol}
                    onChange={e => setSymbol(e.target.value)}
                    required
                    disabled={tickerOptions.length === 0}
                  >
                    <option value="" disabled>Select symbol</option>
                    {tickerOptions.map(t => (
                      <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <div style={{ position: 'relative' }} className="ticker-search-container">
                    <Form.Control
                      type="text"
                      value={searchTerm}
                      onChange={e => {
                        if (!isTickerPreloaded) {
                          const value = e.target.value;
                          setSearchTerm(value);
                          setSymbol(value);
                          setShowDropdown(true);
                          validateSymbol(value);
                        }
                      }}
                      onFocus={() => {
                        if (!isTickerPreloaded) {
                          setShowDropdown(true);
                        }
                      }}
                      placeholder={isTickerPreloaded ? ticker : (dropdownLoading ? 'Loading...' : 'Type to search tickers...')}
                      required
                      disabled={dropdownLoading || isTickerPreloaded}
                      autoComplete="off"
                      isInvalid={shouldRestrictBuySymbol && searchTerm.trim() && !isValidSymbol}
                      readOnly={isTickerPreloaded}
                    />
                    {showDropdown && filteredTickers.length > 0 && !isTickerPreloaded && (
                      <div 
                        className="dropdown-list"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          border: '1px solid var(--bs-border-color)',
                          borderRadius: '0.375rem',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'var(--bs-border-color) var(--bs-body-bg)',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                      >
                        {filteredTickers.map(ticker => (
                          <div
                            key={ticker.symbol}
                            className="dropdown-item"
                            onClick={() => handleTickerSelect(ticker)}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--bs-border-color)',
                              fontSize: '0.875rem'
                            }}
                            onMouseEnter={e => e.target.style.backgroundColor = 'var(--bs-secondary-bg)'}
                            onMouseLeave={e => e.target.style.backgroundColor = 'var(--bs-body-bg)'}
                          >
                            <div className="ticker-symbol" style={{ fontWeight: 'bold' }}>{ticker.symbol}</div>
                            <div className="ticker-name" style={{ fontSize: '0.75rem' }}>
                              {ticker.name}
                              {ticker.type === 'both' && ' (Active + Readylist)'}
                              {ticker.type === 'active' && ' (Active)'}
                              {ticker.type === 'readylist' && ' (Readylist)'}
                              {ticker.type === 'livermore' && ' (Livermore)'}
                              {ticker.type === 'active_livermore' && ' (Active + Livermore)'}
                              {ticker.type === 'readylist_livermore' && ' (Readylist + Livermore)'}
                              {ticker.type === 'active_readylist_livermore' && ' (Active + Readylist + Livermore)'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Select value={type} onChange={e => setType(e.target.value)}>
                  {ORDER_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Action</Form.Label>
                <Form.Select value={action} onChange={e => setAction(e.target.value)}>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Order Date</Form.Label>
                <Form.Control
                  type="date"
                  value={orderDate}
                  onChange={e => setOrderDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          {/* Position Size only for BUY */}
          {action === 'BUY' && (
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>
                    Position Size
                    {(accounts.length > 0 && account?.standard_size) && (
                      <span className="ms-2 text-muted" style={{ fontSize: '0.9em', fontWeight: 'normal' }}>
                        (${(account.standard_size * positionSize).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
                      </span>
                    )}
                    {(singleAccount && singleAccount.standard_size) && (
                      <span className="ms-2 text-muted" style={{ fontSize: '0.9em', fontWeight: 'normal' }}>
                        (${(singleAccount.standard_size * positionSize).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
                      </span>
                    )}
                  </Form.Label>
                  <Form.Select value={positionSize} onChange={handlePositionSizeChange}>
                    {POSITION_SIZES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>{type === 'LIMIT' ? 'Limit Price' : 'Stop Price'}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="any"
                    value={price}
                    onChange={e => handlePriceChange(e.target.value)}
                    onFocus={handlePriceFocus}
                    onBlur={handlePriceBlur}
                    className="order-price-input"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
          {/* For SELL, just price and quantity */}
          {action === 'SELL' && (
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>{type === 'LIMIT' ? 'Limit Price' : 'Stop Price'}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="any"
                    value={price}
                    onChange={e => handlePriceChange(e.target.value)}
                    onFocus={handlePriceFocus}
                    onBlur={handlePriceBlur}
                    className="order-price-input"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row className="mb-3">
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Quantity{action === 'SELL' && maxSellShares !== null ? ` (max ${maxSellShares})` : ''}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="any"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  required
                  max={action === 'SELL' && maxSellShares !== null ? maxSellShares : undefined}
                />
                {action === 'SELL' && (
                  <div className="mt-2 d-flex gap-1 flex-wrap">
                    {[25, 50, 75, 100].map(p => (
                      <Button
                        key={p}
                        size="sm"
                        variant={sellPercent === p ? 'primary' : 'outline-secondary'}
                        onClick={() => handleSellPercentSelect(p)}
                      >
                        {p}%
                      </Button>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <div style={{ 
                  fontSize: '1.5em', 
                  fontWeight: '700', 
                  color: '#007bff',
                  textAlign: 'right',
                  marginTop: '2.25rem'
                }}>
                  {price && quantity ? `$${(Number(price) * Number(quantity)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={6}></Col>
            <Col xs={6}>
              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={onHide} disabled={props.loading} className="flex-fill">
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={props.loading} className="flex-fill">
                  {props.loading ? 'Saving...' : (props.initialData ? 'Save Changes' : 'Create Order')}
                </Button>
              </div>
            </Col>
          </Row>
            </Form>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default OrderDialog; 
